import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Descriptions,
  Row,
  Col,
  Typography,
  message,
  Form,
} from 'antd';
import { useSelector } from 'react-redux';
import { getSalariosMinimos, getUMAs } from 'api/configuracion';
import Moment from 'moment';
import { aplicacionesCargosEnum } from 'views/Tramites/GPM/enums';
import toCurrency from 'utils/toCurrency';
import { monedasEnum } from './FormItemsByMoneda';

const { Text } = Typography;

const CalculoAproximado = ({ setLoading }) => {
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const form = Form.useFormInstance();
  const importe = Form.useWatch('importe', form) || 0;
  const moneda = Form.useWatch('moneda', form);
  const tipo_de_aplicacion = Form.useWatch('tipo_de_aplicacion', form);
  const usar_tipo_de_cambio_actual_por_moneda = Form.useWatch('usar_tipo_de_cambio_actual_por_moneda', form);
  const [salarioMinimo, setSalarioMinimo] = useState(null);
  const [uma, setUma] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [umas, salariosMinimos] = await Promise.all([getUMAs(), getSalariosMinimos()]);
        if (isMounted) {
          const currentYear = Moment().year();
          const finder = usar_tipo_de_cambio_actual_por_moneda
            ? (e) => e.anio === currentYear
            : (e) => e.anio === periodoFiscalSelected.periodo;
          setUma(umas.find(finder) || null);
          setSalarioMinimo(salariosMinimos.find(finder) || null);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, [periodoFiscalSelected, usar_tipo_de_cambio_actual_por_moneda]);

  const total = useMemo(() => {
    const parsedImporte = parseFloat(importe) || 0;
    if (!parsedImporte || !moneda || !tipo_de_aplicacion || (!uma && !salarioMinimo)) return 0;

    switch (moneda) {
      case monedasEnum.PESO:
        return parsedImporte;
      case monedasEnum.SM:
        if (!salarioMinimo) {
          message.warn('NO EXISTE TIPO DE CAMBIO PARA EL SALARIO MÍNIMO DEL PERIODO FISCAL SELECCIONADO');
          return 0;
        }
        return parsedImporte * salarioMinimo.salario_minimo_nominal;
      case monedasEnum.UMA:
        if (!uma) {
          message.warn('NO EXISTE TIPO DE CAMBIO PARA LA UMA DEL PERIODO FISCAL SELECCIONADO');
          return 0;
        }
        return parsedImporte * uma.valor_diario;
      default:
        return 0;
    }
  }, [importe, moneda, tipo_de_aplicacion, uma, salarioMinimo]);

  if (moneda === monedasEnum.PESO || tipo_de_aplicacion !== aplicacionesCargosEnum.FIJO) {
    return null;
  }

  return (
    <Row justify="end">
      <Col xs={24} sm={24} md={8}>
        <Descriptions bordered layout="vertical" size="small">
          {moneda === monedasEnum.UMA && (
            <Descriptions.Item label={<Text>Valor UMA</Text>}>
              <Text style={{ color: !uma ? '#faad14' : '' }}>
                {uma ? toCurrency(uma.valor_diario) : 'No existe tipo de cambio para el periodo'}
              </Text>
            </Descriptions.Item>
          )}
          {moneda === monedasEnum.SM && (
            <Descriptions.Item label={<Text>Valor Salario Mínimo</Text>}>
              <Text style={{ color: !salarioMinimo ? '#faad14' : '' }}>
                {salarioMinimo ? toCurrency(salarioMinimo.salario_minimo_nominal) : 'No existe tipo de cambio para el periodo'}
              </Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label={<Text>Valor estimado</Text>}>
            <Text>{toCurrency(total)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Col>
    </Row>
  );
};

CalculoAproximado.propTypes = {
  setLoading: PropTypes.func.isRequired,
};

export default CalculoAproximado;
